import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAppStore } from '@/stores/app'
import { useUsersStore, type SupportChatIdentity } from '@/stores/users'
import { useProjectsStore } from '@/stores/projects'
import { loadExternalScript } from '@/utils/loadScript'
import { logger } from '@/utils/logger'
import { humanizeEmail } from '@/utils/userDisplay'
import type { SupportChatConfig } from '@/models/PublicConfig'

/**
 * MYAC-1348 — the Plain support chat.
 *
 * Opened from the header help button and from the "contact support" link on the
 * chat's welcome card.
 * The user is signed in by the pair the backend wrote on their own user document
 * (email + HMAC of it); the secret behind that hash never reaches the browser.
 *
 * Everything the ticket carries is sent at OPEN time, not at init: the widget is
 * initialised once per page load, and a context captured then would describe the
 * page the user first landed on, not the one they asked for help from.
 */

export const PLAIN_CHAT_SCRIPT = 'https://chat.cdn-plain.com/index.js'

// Plain's thread-field ENUMs reject unknown values outright, so anything outside
// these sets is omitted rather than sent.
const PROJECT_TYPES = new Set(['qms', 'td', 'regulatory_monitoring'])
const LANGS = new Set(['fr', 'en'])
const USER_AGENT_MAX = 250
// Words that make a mailbox a shared one rather than a person's ("contact@",
// "qualite@"): a name guessed from such an address would greet "Contact".
const SHARED_MAILBOX_WORDS = new Set([
  'accueil', 'admin', 'affairs', 'assurance', 'billing', 'bonjour', 'compta', 'contact',
  'direction', 'hello', 'hr', 'info', 'infos', 'noreply', 'office', 'qa', 'qms', 'quality',
  'qualite', 'raqa', 'reglementaire', 'regulatory', 'rh', 'sales', 'service', 'support',
  'team', 'test',
])

interface PlainThreadField {
  key: string
  type: 'STRING' | 'ENUM'
  stringValue: string
}

interface PlainThreadDetails {
  threadFields?: PlainThreadField[]
  labelTypeIds?: string[]
}

interface PlainChatButton {
  icon: string
  text: string
  threadDetails?: PlainThreadDetails
}

interface PlainCustomerDetails {
  email: string
  emailHash: string
  fullName?: string
}

interface PlainWidget {
  init(options: Record<string, unknown>): void
  update(options: Record<string, unknown>): void
  open(): void
  isInitialized(): boolean
}

declare global {
  interface Window {
    Plain?: PlainWidget
  }
}

export interface SupportChatContext {
  projectId: string | null
  projectType: string | null | undefined
  route: string
  locale: string
  /** Used only when the app's own locale is not one Plain accepts. */
  browserLocale: string
  userAgent: string
}

/** A locale tag ("fr-FR") reduced to the catalogue language ("fr"), or null if Plain does not list it. */
function plainLang(tag: string | undefined): string | null {
  const lang = (tag ?? '').split('-')[0].toLowerCase()
  return LANGS.has(lang) ? lang : null
}

/** The fields attached to every ticket, in the shape Plain's widget expects. */
export function buildThreadFields(ctx: SupportChatContext): PlainThreadField[] {
  const fields: PlainThreadField[] = []
  if (ctx.projectId) {
    fields.push({ key: 'project_id', type: 'STRING', stringValue: ctx.projectId })
  }
  if (ctx.projectType && PROJECT_TYPES.has(ctx.projectType)) {
    fields.push({ key: 'project_type', type: 'ENUM', stringValue: ctx.projectType })
  }
  fields.push({ key: 'route', type: 'STRING', stringValue: ctx.route })
  // The language the user chose in MAC wins; the browser's only fills a gap.
  const lang = plainLang(ctx.locale) ?? plainLang(ctx.browserLocale)
  if (lang) {
    fields.push({ key: 'lang', type: 'ENUM', stringValue: lang })
  }
  if (ctx.userAgent) {
    fields.push({ key: 'user_agent', type: 'STRING', stringValue: ctx.userAgent.substring(0, USER_AGENT_MAX) })
  }
  return fields
}

/**
 * The name the chat greets the user by, and that support sees on the ticket.
 * Their own name when they gave one; otherwise the name guessed from the email by
 * `humanizeEmail`, the guess the task assignee picker already shows
 * (`jane.doe@x.io` → "Jane Doe"). Never the email itself. A guess made only of
 * shared-mailbox words and initials ("Contact", "Test Me", "Yc") is no name, and
 * the user gets Plain's generic greeting.
 */
export function supportChatName(displayName: string | null, email: string): string | null {
  const own = displayName?.trim()
  if (own) return own
  const guess = humanizeEmail(email)
  const parts = guess.toLowerCase().split(' ').filter(Boolean)
  if (parts.every((p) => p.length <= 2 || SHARED_MAILBOX_WORDS.has(p))) return null
  return guess
}

/** Who the chat signs in: the stored identity, plus a name when there is one. */
export function buildCustomerDetails(identity: SupportChatIdentity, displayName: string | null): PlainCustomerDetails {
  const details: PlainCustomerDetails = { email: identity.email, emailHash: identity.email_hash }
  const name = supportChatName(displayName, identity.email)
  if (name) details.fullName = name
  return details
}

/** The three entry buttons. Label ids are workspace data; only the text is translated. */
export function buildChatButtons(
  config: SupportChatConfig,
  t: (key: string) => string,
): PlainChatButton[] {
  const ids = config.label_type_ids
  const button = (icon: string, textKey: string, labelTypeId?: string): PlainChatButton => ({
    icon,
    text: t(textKey),
    ...(labelTypeId ? { threadDetails: { labelTypeIds: [labelTypeId] } } : {}),
  })
  return [
    button('chat', 'help.support_chat.ask_question', ids?.question),
    button('bug', 'help.support_chat.report_problem', ids?.bug),
    button('bulb', 'help.support_chat.suggest_improvement', ids?.improvement),
  ]
}

export function useSupportChat() {
  const appStore = useAppStore()
  const usersStore = useUsersStore()
  const projectsStore = useProjectsStore()
  const { t, locale } = useI18n()

  /** Configured for this environment AND this user has an identity to sign in with. */
  const isAvailable = computed(() => !!appStore.supportChatConfig?.app_id && !!usersStore.supportChatIdentity)

  function context(): SupportChatContext {
    return {
      projectId: projectsStore.currentProjectId,
      projectType: projectsStore.currentProject?.type,
      // The path, never the query string: queries can carry ids and filters that
      // mean nothing to support and should not travel.
      route: window.location.pathname,
      locale: locale.value,
      browserLocale: navigator.language,
      userAgent: navigator.userAgent,
    }
  }

  /**
   * Open the chat with the context of the page the user is on now.
   * Resolves false when the chat cannot open (not configured, no identity, or the
   * vendor script failed), so the caller can fall back to the contact modal.
   */
  async function open(): Promise<boolean> {
    await appStore.loadPublicConfig()
    const config = appStore.supportChatConfig
    const identity = usersStore.supportChatIdentity
    if (!config?.app_id || !identity) return false

    try {
      await loadExternalScript(PLAIN_CHAT_SCRIPT)
    } catch (error) {
      logger.error('Support chat script failed to load', error)
      return false
    }
    const plain = window.Plain
    if (!plain) {
      logger.error('Support chat script loaded but exposed no Plain object')
      return false
    }

    const threadDetails = { threadFields: buildThreadFields(context()) }
    // Rebuilt on every open so the button text follows a language switch.
    const chatButtons = buildChatButtons(config, t)
    if (plain.isInitialized()) {
      plain.update({ threadDetails, chatButtons })
    } else {
      plain.init({
        appId: config.app_id,
        hideLauncher: true,
        // The spec says 'auto', but the app has no dark mode: 'auto' would paint
        // the widget dark over a light app for anyone whose OS is in dark mode.
        theme: 'light',
        style: { brandColor: '#380099' },
        customerDetails: buildCustomerDetails(identity, usersStore.displayName),
        threadDetails,
        chatButtons,
      })
    }
    plain.open()
    return true
  }

  return { isAvailable, open }
}
